import {ReactNode} from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Card,
  Divider,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip
} from "@mantine/core";
import {IconAlertCircle, IconArrowBackUp, IconClock, IconMapPin, IconRefresh, IconRoute, IconUser} from "@tabler/icons-react";
import {
  DispatchRequestTrackingResult,
  DispatchTrackingConfidence,
  DispatchTrackingDestination,
  DispatchTrackingPerformer,
  DispatchTrackingResult,
  DispatchTrackingRoute,
  toDispatchTrackingConfidenceLevel,
  toDispatchTrackingConfidenceReason,
  toDispatchTrackingConfidenceSignal
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {FormattedDateTime} from "../../../date/FormattedDateTime/FormattedDateTime";
import {EntityAnchor} from "../../../anchor/EntityAnchor/EntityAnchor";
import {DefaultText} from "../../../text/DefaultText/DefaultText";
import {DispatchTrackingMap} from "./DispatchTrackingMap";
import {getDispatchTrackingPollingLabel} from "./dispatch-tracking-polling";
import {getFormattedDatabaseCode} from "../../../text/FormattedDatabaseCode/FormattedDatabaseCode";

type TrackingResult = DispatchTrackingResult | DispatchRequestTrackingResult;

export type DispatchTrackingCardProps = {
  tracking?: TrackingResult;
  loading?: boolean;
  fetching?: boolean;
  error?: unknown;
  onRefresh?: () => void;
  refreshInterval?: number;
}

const getConfidenceColor = (confidence?: DispatchTrackingConfidence): string => {
  switch (confidence?.level) {
    case "HIGH":
      return "green";
    case "MEDIUM":
      return "yellow";
    case "LOW":
      return "orange";
    case "NONE":
      return "gray";
    case "UNKNOWN":
    default:
      return "blue";
  }
}

const getDisplayConfidenceLevelCode = (
  performer?: DispatchTrackingPerformer,
  confidence?: DispatchTrackingConfidence
): string | undefined => {
  if (performer && !getLocationDttm(performer) && confidence?.trackable === false) {
    return "UNKNOWN";
  }

  return confidence?.level;
}

const getDisplayConfidenceColor = (
  performer?: DispatchTrackingPerformer,
  confidence?: DispatchTrackingConfidence
): string => {
  const level = getDisplayConfidenceLevelCode(performer, confidence);

  if (level === "UNKNOWN") {
    return "blue";
  }

  return getConfidenceColor(confidence);
}

const getDestinationLabel = (destination?: DispatchTrackingDestination): string | undefined => {
  if (!destination) {
    return undefined;
  }

  const type = destination.type ? `${destination.type.charAt(0)}${destination.type.slice(1).toLowerCase()}` : undefined;

  if (type && destination.siteName) {
    return `${type}: ${destination.siteName}`;
  }

  return destination.siteName ?? type;
}

const getOriginLabel = (performer?: DispatchTrackingPerformer): string | undefined => {
  const origin = performer?.locationContext?.meta?.origin;

  if (!origin) {
    return undefined;
  }

  return origin.siteName ?? origin.source;
}

const getLocationDttm = (performer?: DispatchTrackingPerformer): number | undefined => {
  return performer?.locationDttm ?? performer?.locationContext?.location?.dttm;
}

const getRouteSummary = (route?: DispatchTrackingRoute): string | undefined => {
  if (!route) {
    return undefined;
  }

  const values: string[] = [];

  if (typeof route.distanceMiles === "number") {
    values.push(`${route.distanceMiles.toFixed(1)} mi`);
  }

  if (typeof route.durationMillis === "number") {
    values.push(`${Math.round(route.durationMillis / 60000)} min`);
  }

  return values.length > 0 ? values.join(" / ") : route.status;
}

const isRouteVisible = (confidence?: DispatchTrackingConfidence, route?: DispatchTrackingRoute): boolean => {
  if (!route) {
    return false;
  }

  if (confidence?.trackable === false || confidence?.level === "NONE" || confidence?.level === "UNKNOWN") {
    return false;
  }

  return confidence?.level === "HIGH" || confidence?.level === "MEDIUM";
}

const isDestinationArrived = (destination?: DispatchTrackingDestination): boolean => {
  return destination?.status === "ARRIVED";
}

const isMapVisible = (
  performer?: DispatchTrackingPerformer,
  destination?: DispatchTrackingDestination,
  confidence?: DispatchTrackingConfidence
): boolean => {
  if (confidence?.trackable === false || confidence?.level === "NONE" || confidence?.level === "UNKNOWN") {
    return false;
  }

  return Boolean(
    performer?.locationContext?.location ||
    (typeof destination?.latitude === "number" && typeof destination.longitude === "number")
  );
}

const TrackingField = (props: { icon: ReactNode, label: string, children: ReactNode }) => {
  const {icon, label, children} = props;

  return (
    <Group spacing="xs" noWrap align="flex-start">
      {icon}
      <div>
        <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
          {label}
        </Text>
        <Text size="sm">{children}</Text>
      </div>
    </Group>
  );
}

export const DispatchTrackingCard = (props: DispatchTrackingCardProps) => {
  const {tracking, loading, fetching, error, onRefresh, refreshInterval} = props;
  const {t} = useTranslation(["common"]);

  const performer = tracking?.performer;
  const confidence = tracking?.confidence;
  const warnings = confidence?.warnings ?? [];
  const performerCount = tracking && "performers" in tracking ? tracking.performers?.length : undefined;
  const locationDttm = getLocationDttm(performer);
  const routeSummary = getRouteSummary(tracking?.route);
  const originLabel = getOriginLabel(performer);
  const destinationLabel = getDestinationLabel(tracking?.destination);
  const arrived = isDestinationArrived(tracking?.destination);
  const pollingLabel = getDispatchTrackingPollingLabel(refreshInterval ?? 0);
  const displayConfidenceLevelCode = getDisplayConfidenceLevelCode(performer, confidence);
  const displayConfidenceLevel = displayConfidenceLevelCode
    ? toDispatchTrackingConfidenceLevel(displayConfidenceLevelCode)
    : undefined;
  const displayConfidenceReason = confidence?.reason
    ? toDispatchTrackingConfidenceReason(confidence.reason)
    : undefined;
  const hasAssignedPerformerWithoutLocation = Boolean(performer && !locationDttm && confidence?.trackable === false);
  const showRoute = !arrived && isRouteVisible(confidence, tracking?.route);
  const showMap = isMapVisible(performer, tracking?.destination, confidence);
  const confidenceSignals = confidence?.signals ?? [];

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group position="apart" pb="sm">
        <Group spacing="xs">
          <Title order={4}>{t("common.entity.dispatch.tracking.title", "Active Tracking")}</Title>
          {fetching && !loading && <Loader size="xs"/>}
        </Group>
        <Group spacing="xs">
          {displayConfidenceLevel &&
            <Badge color={getDisplayConfidenceColor(performer, confidence)} variant="light">
              {getFormattedDatabaseCode(displayConfidenceLevel, t)}
            </Badge>
          }
          {typeof performerCount === "number" && performerCount > 1 &&
            <Badge variant="outline">
              {t("common.entity.dispatch.tracking.performercount", "{{count}} drivers", {count: performerCount})}
            </Badge>
          }
          {pollingLabel &&
            <Badge variant="outline" color="gray">
              {t("common.entity.dispatch.tracking.refreshinterval", "{{interval}} refresh", {interval: pollingLabel})}
            </Badge>
          }
          {onRefresh &&
            <Tooltip label={t("common.action.refresh", "Refresh")}>
              <ActionIcon onClick={onRefresh} loading={fetching} variant="subtle">
                <IconRefresh size={16}/>
              </ActionIcon>
            </Tooltip>
          }
        </Group>
      </Group>

      <Divider mb="md"/>

      {loading &&
        <Group position="center" py="xl">
          <Loader/>
        </Group>
      }

      {!loading && error &&
        <Alert icon={<IconAlertCircle size={16}/>} color="red" title={t("common.shared.error", "Error")}>
          {t("common.entity.dispatch.tracking.error", "Tracking could not be loaded.")}
        </Alert>
      }

      {!loading && !error && !performer &&
        <Alert icon={<IconMapPin size={16}/>} color="gray" title={t("common.entity.dispatch.tracking.empty.title", "No active driver")}>
          {tracking?.message ?? confidence?.reason ?? t("common.entity.dispatch.tracking.empty.message", "No active driver/user is currently trackable for this job.")}
        </Alert>
      }

      {!loading && !error && performer &&
        <Stack spacing="md">
          <SimpleGrid cols={2} breakpoints={[{maxWidth: "sm", cols: 1}]}>
            <TrackingField icon={<IconUser size={18}/>} label={t("common.entity.dispatch.tracking.field.driver", "Driver/User")}>
              {performer.personId ? (
                <EntityAnchor
                  value={{
                    objectId: performer.personId,
                    objectName: performer.personName ?? t<string>("common.shared.unspecified", "Unspecified"),
                    objectType: "PERSON"
                  }}/>
              ) : (
                <DefaultText value={performer.personName}/>
              )}
            </TrackingField>
            <TrackingField icon={<IconClock size={18}/>} label={t("common.entity.dispatch.tracking.field.lastlocation", "Last Location")}>
              <FormattedDateTime date={locationDttm}/>
            </TrackingField>
            {originLabel &&
              <TrackingField icon={<IconArrowBackUp size={18}/>} label={t("common.entity.dispatch.tracking.field.source", "Coming From")}>
                <DefaultText value={originLabel}/>
              </TrackingField>
            }
            <TrackingField icon={<IconMapPin size={18}/>} label={t("common.entity.dispatch.tracking.field.destination", "Destination")}>
              {arrived
                ? t("common.entity.dispatch.tracking.destination.arrived", "Arrived at {{destination}}", {
                  destination: destinationLabel ?? t("common.entity.dispatch.tracking.field.destination", "Destination")
                })
                : <DefaultText value={destinationLabel}/>
              }
            </TrackingField>
            {showRoute &&
              <TrackingField icon={<IconRoute size={18}/>} label={t("common.entity.dispatch.tracking.field.route", "Route")}>
                <DefaultText value={routeSummary}/>
              </TrackingField>
            }
          </SimpleGrid>

          {showRoute && tracking?.route?.etaDttm &&
            <Text size="sm" color="dimmed">
              {t("common.entity.dispatch.tracking.eta", "ETA")} <FormattedDateTime date={tracking.route.etaDttm}/>
            </Text>
          }

          {confidence &&
            <Alert icon={<IconAlertCircle size={16}/>} color={getDisplayConfidenceColor(performer, confidence)} title={t("common.entity.dispatch.tracking.confidence.title", "Tracking confidence")}>
              <Stack spacing={4}>
                <Text size="sm">
                  {displayConfidenceReason ? getFormattedDatabaseCode(displayConfidenceReason, t) : t("common.entity.dispatch.tracking.confidence.message", "Confidence is {{level}}.", {
                    level: displayConfidenceLevel ? getFormattedDatabaseCode(displayConfidenceLevel, t) : t("common.shared.unknown", "Unknown")
                  })}
                </Text>
                {confidenceSignals.length > 0 &&
                  <Group spacing={4}>
                    {confidenceSignals.slice(0, 3).map((signal) => (
                      <Badge key={signal} size="xs" variant="outline" color={getDisplayConfidenceColor(performer, confidence)}>
                        {getFormattedDatabaseCode(toDispatchTrackingConfidenceSignal(signal), t)}
                      </Badge>
                    ))}
                  </Group>
                }
              </Stack>
            </Alert>
          }

          {!hasAssignedPerformerWithoutLocation && tracking?.message &&
            <Text size="sm" color="dimmed">
              {tracking.message}
            </Text>
          }

          {hasAssignedPerformerWithoutLocation &&
            <Alert icon={<IconAlertCircle size={16}/>} color="blue" title={t("common.entity.dispatch.tracking.unknown.title", "Tracking location unknown")}>
              {displayConfidenceReason
                ? getFormattedDatabaseCode(displayConfidenceReason, t)
                : tracking?.message ?? t("common.entity.dispatch.tracking.unknown.message", "The assigned performer does not have a recent, trackable location.")}
            </Alert>
          }

          {!hasAssignedPerformerWithoutLocation && warnings.length > 0 &&
            <Alert icon={<IconAlertCircle size={16}/>} color="yellow" title={t("common.entity.dispatch.tracking.warning.title", "Tracking warning")}>
              <Stack spacing={4}>
                {warnings.map((warning) => (
                  <Text key={warning} size="sm">{getFormattedDatabaseCode(toDispatchTrackingConfidenceSignal(warning), t)}</Text>
                ))}
              </Stack>
            </Alert>
          }

          {showMap &&
            <DispatchTrackingMap
              performer={performer}
              destination={tracking?.destination}
              route={showRoute ? tracking?.route : undefined}/>
          }
        </Stack>
      }
    </Card>
  );
}
